import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';

// ── Types ────────────────────────────────────────────────────────────────────

interface Account { id: string; name: string }

interface VatReturn {
  quarter: string;
  country: string;
  vatRate: number;
  period: { start: string; end: string };
  boxes: Record<string, number>;
  refundVat: number;
  status: string;
}

interface VatCountry {
  country: string;
  vatRate: number;
  sales: { net: number; vat: number; gross: number; count: number };
  refunds: { net: number; vat: number; gross: number; count: number };
  netVat: number;
}

interface TaxMatch1099K {
  year: string;
  grossSales: number;
  orderCount: number;
  deductions: {
    fees: number; refunds: number; promotions: number;
    advertising: number; taxCollected: number; storage: number; totalDeductions: number;
  };
  reimbursements: number;
  expectedNet: number;
  actualDeposits: number;
  settlementCount: number;
  difference: number;
  reconciled: boolean;
}

type TabKey = 'vat' | '1099k' | 'eu-oss';

// ── Component ────────────────────────────────────────────────────────────────

export function FinanceTax() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [activeTab, setActiveTab] = useState<TabKey>('vat');
  const [loading, setLoading] = useState(true);

  // VAT state
  const [vatQuarter, setVatQuarter] = useState(getCurrentQuarter());
  const [vatCountry, setVatCountry] = useState('UK');
  const [vatReturn, setVatReturn] = useState<VatReturn | null>(null);

  // 1099-K state
  const [taxYear, setTaxYear] = useState(String(new Date().getFullYear()));
  const [match1099k, setMatch1099k] = useState<TaxMatch1099K | null>(null);

  // EU OSS state
  const [vatCountries, setVatCountries] = useState<VatCountry[]>([]);

  useEffect(() => {
    api.get<Account[]>('/accounts').then((accts) => {
      setAccounts(accts);
      if (accts.length > 0) setSelectedAccount(accts[0].id);
      else setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedAccount) return;
    setLoading(false);
  }, [selectedAccount]);

  async function loadVatReturn() {
    if (!selectedAccount) return;
    setLoading(true);
    try {
      const data = await api.get<VatReturn>(
        `/finance/tax/${selectedAccount}/vat-return?quarter=${vatQuarter}&country=${vatCountry}`,
      );
      setVatReturn(data);
    } catch { setVatReturn(null); }
    finally { setLoading(false); }
  }

  async function load1099K() {
    if (!selectedAccount) return;
    setLoading(true);
    try {
      const data = await api.get<TaxMatch1099K>(
        `/finance/tax/${selectedAccount}/1099k?year=${taxYear}`,
      );
      setMatch1099k(data);
    } catch { setMatch1099k(null); }
    finally { setLoading(false); }
  }

  async function loadEuOss() {
    if (!selectedAccount) return;
    setLoading(true);
    try {
      const [y, q] = vatQuarter.split('-Q');
      const qNum = parseInt(q);
      const startMonth = (qNum - 1) * 3 + 1;
      const endMonth = qNum * 3;
      const endLastDay = new Date(parseInt(y), endMonth, 0).getDate();
      const startDate = `${y}-${String(startMonth).padStart(2, '0')}-01`;
      const endDate = `${y}-${String(endMonth).padStart(2, '0')}-${String(endLastDay).padStart(2, '0')}`;

      const data = await api.get<{ countries: VatCountry[] }>(
        `/finance/tax/${selectedAccount}/vat-by-country?startDate=${startDate}&endDate=${endDate}`,
      );
      setVatCountries(data.countries);
    } catch { setVatCountries([]); }
    finally { setLoading(false); }
  }

  useEffect(() => {
    if (!selectedAccount) return;
    if (activeTab === 'vat') loadVatReturn();
    else if (activeTab === '1099k') load1099K();
    else if (activeTab === 'eu-oss') loadEuOss();
  }, [selectedAccount, activeTab, vatQuarter, vatCountry, taxYear]);

  const fmt = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'vat', label: 'VAT Return' },
    { key: 'eu-oss', label: 'EU VAT by Country' },
    { key: '1099k', label: '1099-K Matching' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">Tax & VAT Reporting</h2>
          <p className="text-sm text-gray-500">VAT returns, 1099-K reconciliation, per-country breakdown.</p>
        </div>
        {accounts.length > 1 && (
          <select value={selectedAccount} onChange={(e) => setSelectedAccount(e.target.value)}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm">
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name || a.id}</option>)}
          </select>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key ? 'border-brand-600 text-brand-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* VAT Return Tab */}
      {activeTab === 'vat' && (
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <div className="flex items-center gap-3 mb-6">
            <select value={vatQuarter} onChange={e => setVatQuarter(e.target.value)}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm">
              {getQuarterOptions().map(q => <option key={q} value={q}>{q}</option>)}
            </select>
            <select value={vatCountry} onChange={e => setVatCountry(e.target.value)}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm">
              {['UK', 'DE', 'FR', 'IT', 'ES', 'NL', 'SE'].map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {loading ? (
            <div className="h-40 animate-pulse rounded bg-gray-100" />
          ) : vatReturn && vatReturn.status !== 'no_data' ? (
            <div className="space-y-4">
              <div className="text-xs text-gray-500 mb-4">
                Period: {vatReturn.period.start} to {vatReturn.period.end} | VAT Rate: {vatReturn.vatRate}%
              </div>

              <div className="grid grid-cols-2 gap-3">
                {Object.entries(vatReturn.boxes).map(([key, val]) => {
                  const label = key.replace(/_/g, ' ').replace(/box(\d)/, 'Box $1:');
                  const isNet = key === 'box5_net_vat';
                  return (
                    <div key={key} className={`rounded-lg border p-3 ${isNet ? 'border-brand-200 bg-brand-50 col-span-2' : 'border-gray-200'}`}>
                      <div className="text-xs text-gray-500 capitalize">{label}</div>
                      <div className={`text-lg font-semibold ${isNet ? 'text-brand-700' : ''}`}>
                        {fmt(val)}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 p-3 rounded-lg bg-gray-50 text-xs text-gray-600">
                <strong>Net VAT {vatReturn.boxes.box5_net_vat >= 0 ? 'owed' : 'refund'}:</strong>{' '}
                {fmt(Math.abs(vatReturn.boxes.box5_net_vat))}
                {vatReturn.refundVat > 0 && <span className="ml-2">(includes {fmt(vatReturn.refundVat)} refund adjustments)</span>}
              </div>

              <button
                onClick={() => {
                  const csv = generateVatCsv(vatReturn);
                  downloadCsv(csv, `vat-return-${vatReturn.country}-${vatReturn.quarter}.csv`);
                }}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm hover:bg-gray-50"
              >
                Export CSV
              </button>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No VAT data for this period. Check that settlements are imported and finance aggregation has run.</p>
          )}
        </div>
      )}

      {/* EU OSS Tab */}
      {activeTab === 'eu-oss' && (
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <div className="flex items-center gap-3 mb-6">
            <select value={vatQuarter} onChange={e => setVatQuarter(e.target.value)}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm">
              {getQuarterOptions().map(q => <option key={q} value={q}>{q}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="h-40 animate-pulse rounded bg-gray-100" />
          ) : vatCountries.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs font-medium uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-2">Country</th>
                    <th className="px-3 py-2">VAT Rate</th>
                    <th className="px-3 py-2 text-right">Sales (Net)</th>
                    <th className="px-3 py-2 text-right">VAT Collected</th>
                    <th className="px-3 py-2 text-right">Refund VAT</th>
                    <th className="px-3 py-2 text-right">Net VAT Due</th>
                    <th className="px-3 py-2 text-right">Transactions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {vatCountries.map(c => (
                    <tr key={c.country} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium">{c.country}</td>
                      <td className="px-3 py-2 text-gray-500">{c.vatRate}%</td>
                      <td className="px-3 py-2 text-right">{fmt(c.sales.net)}</td>
                      <td className="px-3 py-2 text-right">{fmt(c.sales.vat)}</td>
                      <td className="px-3 py-2 text-right text-red-600">{fmt(c.refunds.vat)}</td>
                      <td className="px-3 py-2 text-right font-medium">{fmt(c.netVat)}</td>
                      <td className="px-3 py-2 text-right text-gray-500">{c.sales.count + c.refunds.count}</td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50 font-medium">
                    <td className="px-3 py-2" colSpan={5}>Total</td>
                    <td className="px-3 py-2 text-right">{fmt(vatCountries.reduce((s, c) => s + c.netVat, 0))}</td>
                    <td className="px-3 py-2 text-right text-gray-500">
                      {vatCountries.reduce((s, c) => s + c.sales.count + c.refunds.count, 0)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No EU VAT data for this period.</p>
          )}
        </div>
      )}

      {/* 1099-K Tab */}
      {activeTab === '1099k' && (
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <div className="flex items-center gap-3 mb-6">
            <select value={taxYear} onChange={e => setTaxYear(e.target.value)}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm">
              {[2026, 2025, 2024].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="h-40 animate-pulse rounded bg-gray-100" />
          ) : match1099k ? (
            <div className="space-y-4">
              {/* Waterfall breakdown */}
              <div className="space-y-2">
                <WaterfallRow label="1099-K Gross Sales" amount={match1099k.grossSales} note={`${match1099k.orderCount} orders`} bold />
                <div className="pl-4 space-y-1 border-l-2 border-gray-200 ml-2">
                  <WaterfallRow label="Amazon Fees" amount={-match1099k.deductions.fees} negative />
                  <WaterfallRow label="Refunds" amount={-match1099k.deductions.refunds} negative />
                  <WaterfallRow label="Promotions" amount={-match1099k.deductions.promotions} negative />
                  <WaterfallRow label="Advertising (PPC)" amount={-match1099k.deductions.advertising} negative />
                  <WaterfallRow label="Tax Collected & Remitted" amount={-match1099k.deductions.taxCollected} negative />
                  <WaterfallRow label="Storage Fees" amount={-match1099k.deductions.storage} negative />
                </div>
                <WaterfallRow label="Total Deductions" amount={-match1099k.deductions.totalDeductions} negative bold />
                <WaterfallRow label="Reimbursements" amount={match1099k.reimbursements} />
                <div className="border-t border-gray-200 pt-2">
                  <WaterfallRow label="Expected Net" amount={match1099k.expectedNet} bold />
                </div>
                <WaterfallRow label={`Actual Bank Deposits (${match1099k.settlementCount} settlements)`} amount={match1099k.actualDeposits} bold />

                <div className={`rounded-lg p-3 mt-2 ${
                  match1099k.reconciled ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm font-medium ${match1099k.reconciled ? 'text-green-800' : 'text-amber-800'}`}>
                      Difference
                    </span>
                    <span className={`text-lg font-bold ${match1099k.reconciled ? 'text-green-700' : 'text-amber-700'}`}>
                      {fmt(match1099k.difference)}
                    </span>
                  </div>
                  <p className="text-xs mt-1 text-gray-600">
                    {match1099k.reconciled
                      ? 'Reconciled — difference is under $1.00'
                      : 'Unreconciled — check for missing settlements or timing differences'}
                  </p>
                </div>
              </div>

              <button
                onClick={() => {
                  const csv = generate1099kCsv(match1099k);
                  downloadCsv(csv, `1099k-matching-${taxYear}.csv`);
                }}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm hover:bg-gray-50"
              >
                Export CSV
              </button>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No data for {taxYear}.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function WaterfallRow({ label, amount, note, bold, negative }: {
  label: string; amount: number; note?: string; bold?: boolean; negative?: boolean;
}) {
  const fmt = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
  return (
    <div className={`flex items-center justify-between py-1 ${bold ? 'font-medium' : ''}`}>
      <span className="text-sm text-gray-700">
        {label}
        {note && <span className="ml-2 text-xs text-gray-400">({note})</span>}
      </span>
      <span className={`text-sm ${negative ? 'text-red-600' : amount < 0 ? 'text-red-600' : 'text-gray-900'}`}>
        {fmt(amount)}
      </span>
    </div>
  );
}

function getCurrentQuarter(): string {
  const now = new Date();
  const q = Math.ceil((now.getMonth() + 1) / 3);
  return `${now.getFullYear()}-Q${q}`;
}

function getQuarterOptions(): string[] {
  const options: string[] = [];
  const now = new Date();
  for (let i = 0; i < 8; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i * 3, 1);
    const q = Math.ceil((d.getMonth() + 1) / 3);
    const opt = `${d.getFullYear()}-Q${q}`;
    if (!options.includes(opt)) options.push(opt);
  }
  return options;
}

function generateVatCsv(vat: VatReturn): string {
  const rows = [
    ['VAT Return', vat.country, vat.quarter],
    ['Period', vat.period.start, vat.period.end],
    ['VAT Rate', `${vat.vatRate}%`, ''],
    ['', '', ''],
    ...Object.entries(vat.boxes).map(([key, val]) => [
      key.replace(/_/g, ' ').replace(/box(\d)/, 'Box $1:'), String(val), '',
    ]),
  ];
  return rows.map(r => r.join(',')).join('\n');
}

function generate1099kCsv(data: TaxMatch1099K): string {
  const rows = [
    ['1099-K Reconciliation', data.year, ''],
    ['', '', ''],
    ['Gross Sales (1099-K)', String(data.grossSales), `${data.orderCount} orders`],
    ['Amazon Fees', String(-data.deductions.fees), ''],
    ['Refunds', String(-data.deductions.refunds), ''],
    ['Promotions', String(-data.deductions.promotions), ''],
    ['Advertising', String(-data.deductions.advertising), ''],
    ['Tax Collected', String(-data.deductions.taxCollected), ''],
    ['Storage', String(-data.deductions.storage), ''],
    ['Total Deductions', String(-data.deductions.totalDeductions), ''],
    ['Reimbursements', String(data.reimbursements), ''],
    ['Expected Net', String(data.expectedNet), ''],
    ['Actual Deposits', String(data.actualDeposits), `${data.settlementCount} settlements`],
    ['Difference', String(data.difference), data.reconciled ? 'RECONCILED' : 'UNRECONCILED'],
  ];
  return rows.map(r => r.join(',')).join('\n');
}

function downloadCsv(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
